import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Card, Input, Select, Spin, Tag, TimePicker } from "antd";
import dayjs from "dayjs";
import { PlusOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import css from "./ClientSettingsComponent.module.css";
import { appSettingsActions } from "../../../store/slices/settings-slices";
import { YandexMapSelector } from "../../ui/YandexMapSelector/YandexMapSelector";
import { PhoneCodeSelect } from "../../ui/PhoneCodeSelect/PhoneCodeSelect";

// Default map center (Dubai) used when no coordinates are saved yet
const DEFAULT_LAT = 25.2048;
const DEFAULT_LNG = 55.2708;

const { TextArea } = Input;

const ClientSettingsComponent = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const {
        clientDefaults,
        deliveryIntervals,
        availableCities,
        communicationMethods,
        status,
    } = useSelector((s) => s.appSettings);
    const isLoading = status === "loading";

    const [local, setLocal] = useState({ ...clientDefaults });
    const [localIntervals, setLocalIntervals] = useState([...(deliveryIntervals || [])]);
    const [localCities, setLocalCities] = useState([...(availableCities || [])]);
    const [localCommMethods, setLocalCommMethods] = useState([...(communicationMethods || [])]);

    const [newCity, setNewCity] = useState("");
    const [newCommMethod, setNewCommMethod] = useState("");
    const [startTime, setStartTime] = useState(null);
    const [endTime, setEndTime] = useState(null);

    useEffect(() => { setLocal({ ...clientDefaults }); }, [clientDefaults]);
    useEffect(() => { setLocalIntervals([...(deliveryIntervals || [])]); }, [deliveryIntervals]);
    useEffect(() => { setLocalCities([...(availableCities || [])]); }, [availableCities]);
    useEffect(() => { setLocalCommMethods([...(communicationMethods || [])]); }, [communicationMethods]);

    const set = (key, value) => setLocal((prev) => ({ ...prev, [key]: value }));

    /* ── Delivery intervals ── */
    const addSlot = () => {
        if (!startTime || !endTime) return;
        const slot = `${startTime.format("HH:mm")} - ${endTime.format("HH:mm")}`;
        if (localIntervals.includes(slot)) return;
        setLocalIntervals((prev) => [...prev, slot]);
        setStartTime(null);
        setEndTime(null);
    };
    const removeSlot = (slot) => {
        setLocalIntervals((prev) => prev.filter((s) => s !== slot));
        if (local.deliveryInterval === slot) set("deliveryInterval", "");
    };

    /* ── Cities ── */
    const addCity = () => {
        const city = newCity.trim();
        if (!city || localCities.includes(city)) return;
        setLocalCities((prev) => [...prev, city]);
        setNewCity("");
    };
    const removeCity = (city) => {
        setLocalCities((prev) => prev.filter((c) => c !== city));
        if (local.city === city) set("city", "");
    };

    /* ── Communication methods ── */
    const addCommMethod = () => {
        const method = newCommMethod.trim().toUpperCase();
        if (!method || localCommMethods.includes(method)) return;
        setLocalCommMethods((prev) => [...prev, method]);
        setNewCommMethod("");
    };
    const removeCommMethod = (method) => {
        setLocalCommMethods((prev) => prev.filter((m) => m !== method));
        if (local.communicationMethod === method) set("communicationMethod", "");
    };

    const isDirty =
        JSON.stringify(local) !== JSON.stringify(clientDefaults) ||
        JSON.stringify(localIntervals) !== JSON.stringify(deliveryIntervals || []) ||
        JSON.stringify(localCities) !== JSON.stringify(availableCities || []) ||
        JSON.stringify(localCommMethods) !== JSON.stringify(communicationMethods || []);

    const handleSave = async () => {
        try {
            await dispatch(
                appSettingsActions.updateAppSettings({
                    clientDefaults: local,
                    deliveryIntervals: localIntervals,
                    availableCities: localCities,
                    communicationMethods: localCommMethods,
                    timeFormat: "24h",
                })
            ).unwrap();
            toast.success(t("Client defaults saved successfully"));
        } catch {
            toast.error(t("Failed to save client defaults"));
        }
    };

    return (
        <section className={css.wrap}>
            <Spin spinning={isLoading}>
                <div className={css.pageGrid}>

                    {/* ── LEFT COLUMN: Client Defaults ── */}
                    <div className={css.leftCol}>
                        <Card title={t("Client Defaults")}>
                            <p className={css.hint}>
                                {t("These values pre-fill the create client form for new clients.")}
                            </p>
                            <div className={css.grid}>
                                <div className={css.field}>
                                    <label className={css.label}>{t("Default Phone Code")}</label>
                                    <PhoneCodeSelect
                                        value={local.phoneCode || undefined}
                                        onChange={(val) => set("phoneCode", val ?? "")}
                                        placeholder="+995"
                                    />
                                </div>
                                <div className={css.field}>
                                    <label className={css.label}>{t("Default Communication Details")}</label>
                                    <Input
                                        value={local.communicationDetails || ""}
                                        onChange={(e) => set("communicationDetails", e.target.value)}
                                        placeholder={t("e.g. @username or contact info")}
                                    />
                                </div>
                                <div className={css.field}>
                                    <label className={css.label}>{t("Default City")}</label>
                                    <Select
                                        value={local.city || undefined}
                                        onChange={(val) => set("city", val)}
                                        placeholder={t("Select from cities below")}
                                        allowClear
                                        style={{ width: "100%" }}
                                    >
                                        {localCities.map((city) => (
                                            <Select.Option key={city} value={city}>{city}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className={css.field}>
                                    <label className={css.label}>{t("Default Communication Method")}</label>
                                    <Select
                                        value={local.communicationMethod || undefined}
                                        onChange={(val) => set("communicationMethod", val)}
                                        placeholder={t("Select from methods below")}
                                        allowClear
                                        style={{ width: "100%" }}
                                    >
                                        {localCommMethods.map((m) => (
                                            <Select.Option key={m} value={m}>{m}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className={css.field}>
                                    <label className={css.label}>{t("Default Delivery Interval")}</label>
                                    <Select
                                        value={local.deliveryInterval || undefined}
                                        onChange={(val) => set("deliveryInterval", val)}
                                        placeholder={t("Select from intervals below")}
                                        allowClear
                                        style={{ width: "100%" }}
                                    >
                                        {localIntervals.map((slot) => (
                                            <Select.Option key={slot} value={slot}>{slot}</Select.Option>
                                        ))}
                                    </Select>
                                </div>
                                <div className={css.field} />

                                <div className={`${css.field} ${css.fullSpan}`}>
                                    <label className={css.label}>{t("Default Manager Comment")}</label>
                                    <TextArea
                                        value={local.managerComment || ""}
                                        onChange={(e) => set("managerComment", e.target.value)}
                                        placeholder={t("Default note for kitchen / manager")}
                                        rows={2}
                                    />
                                </div>

                                {/* Map coordinates — editable text inputs */}
                                <div className={css.field}>
                                    <label className={css.label}>{t("Default Latitude")}</label>
                                    <Input
                                        type="number"
                                        value={local.defaultLat ?? ""}
                                        onChange={(e) => set("defaultLat", e.target.value ? parseFloat(e.target.value) : null)}
                                        placeholder="e.g. 25.2048"
                                        step="any"
                                    />
                                </div>
                                <div className={css.field}>
                                    <label className={css.label}>{t("Default Longitude")}</label>
                                    <Input
                                        type="number"
                                        value={local.defaultLng ?? ""}
                                        onChange={(e) => set("defaultLng", e.target.value ? parseFloat(e.target.value) : null)}
                                        placeholder="e.g. 55.2708"
                                        step="any"
                                    />
                                </div>

                                {/* Map picker */}
                                <div className={`${css.field} ${css.fullSpan}`} style={{ marginTop: 8 }}>
                                    <label className={css.label} style={{ marginBottom: 6 }}>{t("Pick Location on Map")}</label>
                                    <div style={{ height: 300, borderRadius: 8, overflow: "hidden", border: "1px solid #d9d9d9" }}>
                                        <YandexMapSelector
                                            key={`map-${!!local.defaultLat}-${!!local.defaultLng}`}
                                            initialLat={local.defaultLat || DEFAULT_LAT}
                                            initialLng={local.defaultLng || DEFAULT_LNG}
                                            onLocationSelect={(lat, lng) => {
                                                set("defaultLat", lat);
                                                set("defaultLng", lng);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* ── RIGHT COLUMN: Lists ── */}
                    <div className={css.rightCol}>

                        {/* Available Cities */}
                        <Card title={t("Available Cities")}>
                            <p className={css.hint}>
                                {t("Cities shown in the client city dropdown.")}
                            </p>
                            <div className={css.tagRow}>
                                {localCities.length === 0
                                    ? <span className={css.emptyHint}>{t("No cities defined yet")}</span>
                                    : localCities.map((city) => (
                                        <Tag key={city} closable onClose={() => removeCity(city)} style={{ fontSize: 13, padding: "4px 10px" }}>
                                            {city}
                                        </Tag>
                                    ))
                                }
                            </div>
                            <div className={css.addRow}>
                                <Input
                                    value={newCity}
                                    onChange={(e) => setNewCity(e.target.value)}
                                    onPressEnter={addCity}
                                    placeholder={t("e.g. Dubai")}
                                    style={{ width: 200 }}
                                />
                                <Button icon={<PlusOutlined />} onClick={addCity} disabled={!newCity.trim()}>
                                    {t("Add")}
                                </Button>
                            </div>
                        </Card>

                        {/* Communication Methods */}
                        <Card title={t("Communication Methods")}>
                            <p className={css.hint}>
                                {t("Methods shown in the client communication method dropdown.")}
                            </p>
                            <div className={css.tagRow}>
                                {localCommMethods.length === 0
                                    ? <span className={css.emptyHint}>{t("No methods defined yet")}</span>
                                    : localCommMethods.map((m) => (
                                        <Tag key={m} closable onClose={() => removeCommMethod(m)} style={{ fontSize: 13, padding: "4px 10px" }}>
                                            {m}
                                        </Tag>
                                    ))
                                }
                            </div>
                            <div className={css.addRow}>
                                <Input
                                    value={newCommMethod}
                                    onChange={(e) => setNewCommMethod(e.target.value)}
                                    onPressEnter={addCommMethod}
                                    placeholder={t("e.g. TELEGRAM")}
                                    style={{ width: 200 }}
                                />
                                <Button icon={<PlusOutlined />} onClick={addCommMethod} disabled={!newCommMethod.trim()}>
                                    {t("Add")}
                                </Button>
                            </div>
                        </Card>

                        {/* Available Delivery Intervals */}
                        <Card
                            title={
                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                    {t("Available Delivery Intervals")}
                                    <span className={css.badge24h}>24H</span>
                                </div>
                            }
                        >
                            <p className={css.hint}>
                                {t("These time slots are shown to clients on the profile page.")}
                            </p>
                            <div className={css.tagRow}>
                                {localIntervals.length === 0
                                    ? <span className={css.emptyHint}>{t("No intervals defined yet")}</span>
                                    : localIntervals.map((slot) => (
                                        <Tag key={slot} closable onClose={() => removeSlot(slot)} style={{ fontSize: 13, padding: "4px 10px" }}>
                                            {slot}
                                        </Tag>
                                    ))
                                }
                            </div>
                            <div className={css.addRow}>
                                <TimePicker
                                    value={startTime}
                                    onChange={setStartTime}
                                    format="HH:mm"
                                    placeholder={t("Start")}
                                    minuteStep={15}
                                    showNow={false}
                                    needConfirm={false}
                                    style={{ width: 120 }}
                                />
                                <span style={{ color: "#999" }}>–</span>
                                <TimePicker
                                    value={endTime}
                                    onChange={setEndTime}
                                    format="HH:mm"
                                    placeholder={t("End")}
                                    minuteStep={15}
                                    showNow={false}
                                    needConfirm={false}
                                    style={{ width: 120 }}
                                />
                                <Button icon={<PlusOutlined />} onClick={addSlot} disabled={!startTime || !endTime}>
                                    {t("Add")}
                                </Button>
                            </div>
                        </Card>

                    </div>
                </div>

                <div className={css.actions}>
                    <Button type="primary" onClick={handleSave} loading={isLoading} disabled={!isDirty}>
                        {t("Save client defaults")}
                    </Button>
                </div>
            </Spin>
        </section>
    );
};

export { ClientSettingsComponent };
